import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { Text, theme } from '@theguarantors/tg-package-material-ui';

import type { InviteEligibilityOutcome } from '@/modules/application-tracker/api';

import { buildInviteEligibilitySearchPath } from '../utils/build-invite-eligibility-search-path.util';
import {
  getInviteEligibilityBannerContent,
  type InviteEligibilityBannerVariant,
} from './invite-eligibility-banner.copy';

export type InviteEligibilityBannerProps = {
  outcome: InviteEligibilityOutcome;
  email: string;
  totalMatches?: number;
  /** Reserved for future reason-specific copy; banners today key off outcome only. */
  reason?: string;
  onCtaClick?: () => void;
};

const VARIANT_STYLES: Record<
  InviteEligibilityBannerVariant,
  { backgroundColor: string; ctaColor: string; ctaBorderColor: string; borderColor: string }
> = {
  warning: {
    // Figma / design-system: orange.100 === #FFF2D3
    backgroundColor: theme.palette.orange[100] ?? '#FFF2D3',
    ctaColor: theme.palette.neutralGrey[700] ?? '#4F4F4F',
    ctaBorderColor: theme.palette.neutralGrey[200] ?? '#D1D1D1',
    borderColor: 'transparent',
  },
  block: {
    backgroundColor: theme.palette.red[50] ?? '#FDF2F1',
    ctaColor: theme.palette.red[600] ?? '#DB3429',
    ctaBorderColor: theme.palette.red[600] ?? '#DB3429',
    borderColor: 'transparent',
  },
};

export function InviteEligibilityBanner({
  outcome,
  email,
  totalMatches,
  reason,
  onCtaClick,
}: InviteEligibilityBannerProps) {
  const content = getInviteEligibilityBannerContent({ outcome, totalMatches, reason });
  if (!content || !email.trim()) {
    return null;
  }

  const styles = VARIANT_STYLES[content.variant];
  const href = buildInviteEligibilitySearchPath(email);

  return (
    <Box
      data-testid={`invite-eligibility-banner-${content.variant}`}
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        // Parent applicants Stack already provides 8px above this notice — no extra mt.
        px: 2,
        py: 1.5,
        borderRadius: '8px',
        border: `1px solid ${styles.borderColor}`,
        backgroundColor: styles.backgroundColor,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Text.BodySmall
          component="p"
          data-testid="invite-eligibility-banner-title"
          sx={{
            m: 0,
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '22px',
            color: theme.palette.neutralGrey[950] ?? '#1D1D1D',
          }}
        >
          {content.title}
        </Text.BodySmall>
        <Text.BodySmall
          component="p"
          data-testid="invite-eligibility-banner-body"
          sx={{
            m: 0,
            mt: 0.25,
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '22px',
            color: theme.palette.neutralGrey[700] ?? '#4F4F4F',
          }}
        >
          {content.body}
        </Text.BodySmall>
      </Box>

      <ButtonBase
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onCtaClick}
        data-testid="invite-eligibility-banner-cta"
        aria-label={`${content.ctaLabel} applications for ${email.trim()}`}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          px: 1.5,
          py: 0.75,
          borderRadius: '8px',
          border: `1px solid ${styles.ctaBorderColor}`,
          backgroundColor: styles.backgroundColor,
          color: styles.ctaColor,
          textDecoration: 'none',
          '&:hover': {
            backgroundColor: styles.backgroundColor,
            opacity: 0.9,
          },
        }}
      >
        <ArrowForwardIcon aria-hidden={true} sx={{ width: 18, height: 18 }} />
        <Text.BodySmall
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '22px',
            color: 'inherit',
            whiteSpace: 'nowrap',
          }}
        >
          {content.ctaLabel}
        </Text.BodySmall>
      </ButtonBase>
    </Box>
  );
}
